<script setup lang="ts">
const { t } = useI18n();

const placeholders = computed(() => Array.from({ length: 10 }, (_, i) => t(`hero.placeholders.${i}`)));

const exampleKeys = ['cgv', 'location', 'bail', 'prestation', 'associes', 'vente', 'travail'] as const;
const examples = computed(() => exampleKeys.map(key => ({
  label: t(`hero.examples.${key}.label`),
  prompt: t(`hero.examples.${key}.prompt`),
})));

const inputPrompt = ref("");
const placeholder = ref(placeholders.value[0]);
const placeholderIndex = ref(0);
onMounted(() => {
  setInterval(() => {
    placeholderIndex.value = (placeholderIndex.value + 1) % placeholders.value.length;
    placeholder.value = placeholders.value[placeholderIndex.value];
  }, 2000);
});

function trackGenerate() {
  safeCapture("landing_generate_click", {
    promptLength: inputPrompt.value.length,
  });
}

function trackExample(label: string) {
  safeCapture("landing_example_prompt_click", { label });
}
</script>

<template>
  <div class="relative isolate pt-14">
    <div
      class="absolute inset-x-0 -top-40 -z-10 transform-gpu overflow-hidden blur-3xl sm:-top-80"
      aria-hidden="true"
    >
      <div
        class="relative left-[calc(50%-11rem)] aspect-[1155/678] w-[36.125rem] -translate-x-1/2 rotate-[30deg] bg-gradient-to-tr from-[#ff80b5] to-[#9089fc] opacity-30 sm:left-[calc(50%-30rem)] sm:w-[72.1875rem]"
        style="clip-path: polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)"
      />
    </div>
    <div class="my-24">
      <div class="mx-auto max-w-7xl px-6 lg:px-8">
        <div class="mx-auto max-w-2xl text-center">
          <h1 class="text-balance font-serif text-4xl font-semibold text-gray-900 sm:text-7xl">
            {{ $t('hero.title') }}<span class="text-primary-600">{{ $t('hero.titleHighlight') }}</span>{{ $t('hero.titleEnd') }}
          </h1>
          <p class="mx-auto mt-8 max-w-xl text-pretty font-medium text-gray-500 sm:text-lg">
            {{ $t('hero.subtitle') }}
          </p>
          <div class="flex items-center justify-center gap-x-6 sm:mt-10">
            <!-- IA input -->
            <div class="my-10 w-[700px]">
              <!-- eslint-disable-next-line tailwindcss/no-custom-classname -->
              <div class="flex rounded-lg text-sm shadow-lg sm:text-base">
                <input
                  id="prompt"
                  v-model="inputPrompt"
                  type="text"
                  name="prompt"
                  class="block w-full rounded-none rounded-l-lg border-0 py-2.5 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary-600"
                  :placeholder="placeholder"
                >
                <NuxtLinkLocale
                  :to="{ path: '/app/new', query: { prompt: inputPrompt } }"
                  class="relative -ml-px inline-flex items-center gap-x-1.5 rounded-r-lg bg-primary-600 px-3 py-2 font-semibold text-white ring-1 ring-inset"
                  @click="trackGenerate"
                >
                  <Icon
                    name="heroicons:sparkles"
                    class="-ml-0.5 size-5"
                    aria-hidden="true"
                  />
                  {{ $t('hero.generate') }}
                </NuxtLinkLocale>
              </div>
            </div>
          </div>

          <div class="mx-auto flex max-w-xl flex-wrap justify-center gap-2">
            <NuxtLinkLocale
              v-for="{ label, prompt } in examples"
              :key="label"
              :to="{ path: '/app/new', query: { prompt } }"
              class="inline-flex items-center rounded-full bg-primary-50 px-2 py-1 text-xs font-medium text-primary-700 ring-1 ring-inset ring-primary-600/10"
              @click="trackExample(label)"
            >
              {{ label }}
            </NuxtLinkLocale>
          </div>
        </div>
      </div>
    </div>
    <div
      class="absolute inset-x-0 top-[calc(100%-13rem)] -z-10 transform-gpu overflow-hidden blur-3xl sm:top-[calc(100%-30rem)]"
      aria-hidden="true"
    >
      <div
        class="relative left-[calc(50%+3rem)] aspect-[1155/678] w-[36.125rem] -translate-x-1/2 bg-gradient-to-tr from-[#ff80b5] to-[#9089fc] opacity-30 sm:left-[calc(50%+36rem)] sm:w-[72.1875rem]"
        style="clip-path: polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)"
      />
    </div>
  </div>
</template>

<style scoped>
@keyframes glowAnimation {
  0%, 100% {
    box-shadow: 0 0 55px theme('colors.blue.300 / 50%'),
                0 0 60px theme('colors.blue.300 / 50%'),
                0 0 65px theme('colors.blue.300 / 50%');
  }
  50% {
    box-shadow: 0 0 60px theme('colors.purple.300 / 50%'),
                0 0 70px theme('colors.purple.300 / 50%'),
                0 0 80px theme('colors.purple.300 / 50%');
  }
}

.shadow-glow {
  animation: glowAnimation 10s infinite;
}
</style>
